// frontend/src/App.jsx
// FRONTEND_CODE_VERSION = "v1.0.6" // Added route-based code splitting for performance
import React, { useEffect, Suspense, lazy } from "react"; // Import Suspense and lazy for code splitting
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { FilterProvider, useFilterContext } from "./contexts/FilterContext.jsx";
import { TrackingProvider } from "./contexts/TrackingContext.jsx";

// Lazy load all page components for code splitting (reduces initial bundle size)
const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const AffiliateDisclosurePage = lazy(
  () => import("./pages/AffiliateDisclosurePage"),
);
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));
const AccessibilityDeclarationPage = lazy(
  () => import("./pages/AccessibilityDeclarationPage"),
);
const CookiesAndTrackingPolicyPage = lazy(
  () => import("./pages/CookiesAndTrackingPolicyPage"),
);
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const UnifiedAuthPage = lazy(() => import("./pages/UnifiedAuthPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerificationSuccessPage = lazy(
  () => import("./pages/VerificationSuccessPage"),
);
const AlreadyVerifiedPage = lazy(() => import("./pages/AlreadyVerifiedPage"));
const EmailVerificationPage = lazy(
  () => import("./pages/EmailVerificationPage"),
);
const TestEnhancedComponents = lazy(
  () => import("./components/TestEnhancedComponents"),
);
const CrossBrowserTest = lazy(() => import("./components/CrossBrowserTest"));
const CleanupReviewsPage = lazy(() => import("./pages/CleanupReviewsPage"));
const ScrapingHealthPage = lazy(
  () => import("./pages/Admin/ScrapingHealthPage"),
);
const CollectionManagementPage = lazy(
  () => import("./pages/CollectionManagementPage"),
);
const EnhancedServiceFlowControlPage = lazy(
  () => import("./pages/EnhancedServiceFlowControlPage"),
);

// Keep Header, Footer, and BackToTopButton as regular imports (above the fold, needed immediately)
import Header from "./components/Header"; // Import the new Header component
import Footer from "./components/Footer"; // Import the Footer component
import BackToTopButton from "./components/BackToTopButton"; // Import back to top button
import UmamiAnalytics from "./components/UmamiAnalytics"; // Import Umami Analytics
import HomePageHeaderWrapper from "./components/HomePageHeaderWrapper"; // Import for merged header navigation

// Loading component for Suspense fallback
const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

import MobileNavigation from "./components/MobileNavigation"; // Import mobile navigation
import PWAManager from "./components/PWAManager"; // Import PWA manager
import TouchGestureManager from "./components/TouchGestureManager.jsx"; // Import touch gesture manager
import PushNotificationManager from "./components/PushNotificationManager.jsx"; // Import push notification manager
import MobileUIOptimizer from "./components/MobileUIOptimizer.jsx"; // Import mobile UI optimizer
import { useAuth } from "./contexts/AuthContext.jsx";
// Removed debugEnvVars import and call after env validation

// Component to handle language detection from URL parameters
function LanguageDetector() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const langParam = searchParams.get("lang");
    if (langParam && (langParam === "he" || langParam === "en")) {
      console.log(`[LanguageDetector] Setting language from URL: ${langParam}`);
      i18n.changeLanguage(langParam);
    }
  }, [searchParams, i18n]);

  return null;
}

function App() {
  const { i18n, t } = useTranslation(); // Get i18n instance and t function
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isMobileLandscape, setIsMobileLandscape] = React.useState(false);
  const [isLandscape, setIsLandscape] = React.useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(44);

  console.log(
    "App.jsx: isAuthenticated from useAuth:",
    isAuthenticated,
    "isLoading:",
    isLoading,
  ); // DEBUGGING LINE

  // Detect landscape and header height for main padding
  useEffect(() => {
    const checkLandscape = () => {
      const isMobile = window.innerWidth < 640;
      const isLandscapeValue = window.matchMedia(
        "(orientation: landscape)",
      ).matches;
      setIsMobileLandscape(isMobile && isLandscapeValue);
      setIsLandscape(isLandscapeValue);

      // Get actual header height
      setTimeout(() => {
        const header = document.querySelector("header");
        if (header) {
          const height = header.getBoundingClientRect().height;
          setHeaderHeight(height);
        }
      }, 100);
    };
    checkLandscape();
    window.addEventListener("resize", checkLandscape);
    window.addEventListener("orientationchange", checkLandscape);
    return () => {
      window.removeEventListener("resize", checkLandscape);
      window.removeEventListener("orientationchange", checkLandscape);
    };
  }, []);

  // Effect to update HTML direction and language
  useEffect(() => {
    document.documentElement.dir = i18n.dir(); // e.g., 'rtl' or 'ltr'
    document.documentElement.lang = i18n.language; // e.g., 'he' or 'en'
  }, [i18n, i18n.language]); // Rerun when i18n instance or language changes

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t("loading_alertique")}</p>
        </div>
      </div>
    );
  }

  return (
    <PWAManager
      showInstallPrompt={false}
      showOfflineIndicator={true}
      hideBanners={false}
    >
      <UmamiAnalytics /> {/* Umami Analytics - Privacy-first web analytics */}
      <PushNotificationManager showDebugPanel={false}>
        <MobileUIOptimizer showDebugPanel={false}>
          {/* Temporarily disabled TouchGestureManager to fix touch issues */}
          {/* <TouchGestureManager 
          showDebugOverlay={false}
          onSwipe={(e, direction) => {
            // Add swipe gestures for navigation
            console.log('Swipe detected:', direction);
          }}
          onLongPress={(e, position) => {
            // Add haptic feedback for long press
            if (navigator.vibrate) {
              navigator.vibrate(100);
            }
          }}
        > */}
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <HomePageHeaderWrapper>
              <LanguageDetector /> {/* Handle language detection from URL */}
              <Header /> {/* Render Header above all routes */}
              <main
                className="pt-[44px] sm:pt-20 pb-4 landscape:pt-0 sm:landscape:pt-20"
                style={
                  isLandscape
                    ? {
                        paddingTop: `${headerHeight}px`,
                      }
                    : {}
                }
              >
                {" "}
                {/* Add padding to main content and bottom padding for mobile nav */}
                <Suspense fallback={<PageLoadingFallback />}>
                  <Routes>
                    {/* Public Homepage - accessible to everyone */}
                    <Route path="/" element={<HomePage />} />

                    {/* About page */}
                    <Route path="/about" element={<AboutPage />} />
                    {/* Help page */}
                    <Route path="/help" element={<HelpPage />} />
                    {/* Privacy Policy page */}
                    <Route path="/privacy" element={<PrivacyPolicyPage />} />
                    {/* Affiliate Disclosure page */}
                    <Route
                      path="/affiliate-disclosure"
                      element={<AffiliateDisclosurePage />}
                    />
                    {/* Terms of Use page */}
                    <Route path="/terms" element={<TermsOfUsePage />} />
                    {/* Accessibility Declaration page */}
                    <Route
                      path="/accessibility"
                      element={<AccessibilityDeclarationPage />}
                    />
                    {/* Cookies & Tracking Policy page */}
                    <Route
                      path="/cookies"
                      element={<CookiesAndTrackingPolicyPage />}
                    />

                    {/* Auth-related routes */}
                    <Route path="/auth" element={<UnifiedAuthPage />} />
                    {/* Backward compatibility routes - redirect to /auth */}
                    <Route
                      path="/login"
                      element={<Navigate to="/auth?mode=login" replace />}
                    />
                    <Route
                      path="/register"
                      element={<Navigate to="/auth?mode=register" replace />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path="/reset-password"
                      element={<ResetPasswordPage />}
                    />
                    <Route
                      path="/verify-email"
                      element={<EmailVerificationPage />}
                    />
                    <Route
                      path="/verify"
                      element={<VerificationSuccessPage />}
                    />
                    <Route
                      path="/already-verified"
                      element={<AlreadyVerifiedPage />}
                    />

                    {/* Test route for enhanced components */}
                    <Route
                      path="/test-components"
                      element={<TestEnhancedComponents />}
                    />

                    {/* Cross-browser compatibility test route */}
                    <Route
                      path="/test-cross-browser"
                      element={<CrossBrowserTest />}
                    />

                    {/* Protected Cleanup Reviews route */}
                    <Route
                      path="/cleanup/reviews"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated ? (
                          <CleanupReviewsPage />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Dashboard route - wait for auth to load so post-login redirect doesn't flash to login */}
                    <Route
                      path="/dashboard"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated ? (
                          <DashboardPage />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Admin Scraping Health route */}
                    <Route
                      path="/admin/scraping-health"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated && user?.role_level >= 3 ? (
                          <ScrapingHealthPage />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* /admin/email-status redirects to dashboard (Email & Quota is now a panel in Admin tab) */}
                    <Route
                      path="/admin/email-status"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Admin Collection Management route */}
                    <Route
                      path="/admin/collections"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated && user?.role_level >= 3 ? (
                          <CollectionManagementPage />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Admin Service Flow Control route */}
                    <Route
                      path="/admin/service-flow"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated && user?.role_level >= 3 ? (
                          <EnhancedServiceFlowControlPage />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Deals route - redirects to dashboard for now */}
                    <Route
                      path="/deals"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Protected Profile route - redirects to dashboard for now */}
                    <Route
                      path="/profile"
                      element={
                        isLoading ? (
                          <PageLoadingFallback />
                        ) : isAuthenticated ? (
                          <Navigate to="/dashboard" replace />
                        ) : (
                          <Navigate to="/login" replace />
                        )
                      }
                    />

                    {/* Redirect any other unmatched routes to the homepage */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </main>
              {/* Footer - Universal component */}
              <Footer />
              {/* Back to Top Button - Universal component */}
              <BackToTopButton />
              {/* Removed MobileNavigation - using hamburger menu instead */}
            </HomePageHeaderWrapper>
          </Router>
          {/* </TouchGestureManager> */}
        </MobileUIOptimizer>
      </PushNotificationManager>
    </PWAManager>
  );
}

export default App;
// Vercel deployment trigger - Thu Oct  9 22:19:31 IDT 2025
